import { useState, useEffect } from 'react'
import { RouteData, POI } from '@cya-routes/shared'
import { ElevationProfile } from '../editor/ElevationProfile'
import { POIReferencePanel } from '../editor/POIReferencePanel'
import { POIReferencePanelWithReports } from '../editor/POIReferencePanelWithReports'
import { calculatePOIDistance } from '../../services/poiDistanceService'
import { getPOITypeById, getPOICategoryById } from '../../data/poiTypes'
import { 
  ChevronLeft, 
  ChevronRight, 
  ArrowRight, 
  ArrowUpRight, 
  ArrowDownRight, 
  RectangleEllipsis,
  ChartArea,
  Info,
  ChevronDown,
  ChevronUp,
  Download,
  Bookmark,
  BookmarkCheck,
  AlertTriangle,
  MessageSquare
} from 'lucide-react'
import { downloadGPX } from '../../utils/gpxExport'
import { BookmarkButton } from '../common/BookmarkButton'
import { useAppStore } from '@cya-routes/shared'
import { checkBookmarkExists } from '../../services/firebase/bookmarks'
import { DiscussionTab } from './DiscussionTab'
import { GPXDownloadModal } from '../modals/GPXDownloadModal'

interface ElevationProfileSectionProps {
  showElevation: boolean
  selectedRouteId: string
  getCurrentRoute: () => RouteData | null
  routes: RouteData[]
  pois: POI[]
  routeInfo: {
    description: string
    name?: string
    routeType?: string
    club?: {
      name?: string
      slug?: string
    }
  }
  navigateToRoute: (direction: 'prev' | 'next') => void
  getNavigableRoutes: () => RouteData[]
  handlePOISelect: (poi: POI) => void
  setShowElevation: (show: boolean) => void
  onHoverDistanceChange?: (distance: number | null) => void
  onClimbHover?: (climbInfo: { index: number, climb: any } | null) => void
  onMinimizedChange?: (minimized: boolean) => void
  onActiveTabChange?: (tab: string) => void
  scaleFactor?: number
  initialMinimized?: boolean
  routeId?: string
  // User POI related props
  userPOIs?: any[]
  showUserPOIs?: boolean
  onToggleUserPOIs?: () => void
  onUserPOIClick?: (poi: any) => void
  showResolved?: boolean
  onToggleResolved?: () => void
  hasUserPOIs?: boolean
  overlayMode?: boolean // New prop to enable overlay positioning mode
  flybyProgress?: number // 0-100 percentage of flyby progress
  iconOnlyButtons?: boolean // Force buttons to show as icons only
}

// Tooltip component for consistent styling
const TooltipWrapper = ({ children, text }: { children: React.ReactNode; text: string }) => (
  <div className="relative group">
    {children}
    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-emerald-600/90 backdrop-blur-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 shadow-lg border border-emerald-500/20">
      {text}
    </span>
  </div>
)

export function ElevationProfileSection({
  showElevation,
  selectedRouteId,
  getCurrentRoute,
  routes,
  pois,
  routeInfo,
  navigateToRoute,
  getNavigableRoutes,
  handlePOISelect,
  setShowElevation,
  onHoverDistanceChange,
  onClimbHover,
  onMinimizedChange,
  onActiveTabChange,
  scaleFactor = 1,
  initialMinimized = false,
  routeId,
  userPOIs = [],
  showUserPOIs = false,
  onToggleUserPOIs,
  onUserPOIClick,
  showResolved = false,
  onToggleResolved,
  hasUserPOIs = false,
  overlayMode = false,
  flybyProgress,
  iconOnlyButtons = false
}: ElevationProfileSectionProps) {
  const [activeTab, setActiveTab] = useState('elevation')
  const [isMinimized, setIsMinimized] = useState(initialMinimized)
  const [isMobile, setIsMobile] = useState(false)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [showGPXModal, setShowGPXModal] = useState(false)
  
  const { auth } = useAppStore()
  const user = auth.currentUser
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Notify parent when minimized state changes
  useEffect(() => {
    onMinimizedChange?.(isMinimized)
  }, [isMinimized, onMinimizedChange])

  // Notify parent when active tab changes
  useEffect(() => {
    onActiveTabChange?.(activeTab)
  }, [activeTab, onActiveTabChange])

  if (!showElevation || !selectedRouteId || !getCurrentRoute()) {
    return null
  }

  const currentRoute = getCurrentRoute()

  const getHeight = () => {
    if (isMinimized) return isMobile ? '65px' : '40px' // Taller header on mobile when minimized
    if (activeTab === 'segment' || activeTab === 'discussion') return isMobile ? '450px' : '550px'
    return isMobile ? '290px' : '250px' // Taller for mobile
  }

  return (
    <>
    <div 
      style={{ 
        height: getHeight(),
        minHeight: getHeight(),
        paddingBottom: isMobile ? 'max(1rem, env(safe-area-inset-bottom))' : undefined,
        ...(overlayMode && isMinimized ? { transform: `translateY(calc(100% - ${isMobile ? '65px' : '40px'}))` } : {})
      }} 
      className={`${
        overlayMode 
          ? 'fixed bottom-0 left-0 right-0 z-50' 
          : 'border-t flex-shrink-0'
      } border-white/10 bg-gray-800/70 backdrop-blur-sm shadow-xl transition-all duration-300`}
    >
      <div className="h-full flex flex-col">
          <div className={`${isMobile ? 'flex-col space-y-1' : 'flex-row items-center justify-between'} flex ${isMobile ? 'pl-3 pr-2 py-1' : 'p-1'} ${!isMinimized ? 'border-b border-white/10' : ''}`}>
            {/* First row on mobile: tabs and title/navigation */}
            <div className={`flex items-center ${isMobile ? 'justify-between w-full' : 'space-x-2'}`}>
              <div className={`flex items-center space-x-2 ${isMobile ? '' : 'ml-2'}`}>
                <div className="relative group">
                  <button 
                    onClick={() => {
                      setActiveTab('elevation')
                      setIsMinimized(false)
                    }}
                    className={`px-2 py-1 text-xs rounded-lg transition-all flex items-center justify-center backdrop-blur-sm border ${
                      activeTab === 'elevation' 
                        ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-600/30' 
                        : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <ChartArea className={iconOnlyButtons ? "w-3.5 h-3.5" : "w-3.5 h-3.5 md:hidden"} />
                    {!iconOnlyButtons && (
                      <span className="hidden md:inline-flex items-center space-x-1">
                        <ChartArea className="w-3 h-3" />
                        <span>Ele.</span>
                      </span>
                    )}
                  </button>
                  <span className="absolute bottom-full left-full mb-2 ml-2 px-3 py-1.5 text-xs text-white bg-emerald-600/90 backdrop-blur-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 shadow-lg border border-emerald-500/20">
                    Elevation Profile
                  </span>
                </div>
                <TooltipWrapper text="Route Overview">
                  <button 
                    onClick={() => {
                      setActiveTab('segment')
                      setIsMinimized(false)
                    }}
                    className={`px-2 py-1 text-xs rounded-lg transition-all flex items-center justify-center backdrop-blur-sm border ${
                      activeTab === 'segment' 
                        ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-600/30' 
                        : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Info className={iconOnlyButtons ? "w-3.5 h-3.5" : "w-3.5 h-3.5 md:hidden"} />
                    {!iconOnlyButtons && (
                      <span className="hidden md:inline-flex items-center space-x-1">
                        <Info className="w-3 h-3" />
                        <span>Info</span>
                      </span>
                    )}
                  </button>
                </TooltipWrapper>
                <TooltipWrapper text="Discussion">
                  <button 
                    onClick={() => {
                      setActiveTab('discussion')
                      setIsMinimized(false)
                    }}
                    className={`px-2 py-1 text-xs rounded-lg transition-all flex items-center justify-center backdrop-blur-sm border ${
                      activeTab === 'discussion' 
                        ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-600/30' 
                        : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <MessageSquare className={iconOnlyButtons ? "w-3.5 h-3.5" : "w-3.5 h-3.5 md:hidden"} />
                    {!iconOnlyButtons && (
                      <span className="hidden md:inline-flex items-center space-x-1">
                        <MessageSquare className="w-3 h-3" />
                        <span>Discuss</span>
                      </span>
                    )}
                  </button>
                </TooltipWrapper>
                <TooltipWrapper text="Save Route">
                  <BookmarkButton
                    routeId={routeId || ''}
                    routeData={{
                      routeId: routeId || '',
                      routeName: routeInfo.name || '',
                      routeThumbnail: undefined,
                      routeDistance: currentRoute?.stats?.distance,
                      routeElevation: currentRoute?.elevation?.up,
                      routeLocation: currentRoute?.location?.state,
                      routeType: routeInfo.routeType,
                      clubId: routeInfo.club?.slug,
                      clubName: routeInfo.club?.name
                    }}
                    size="sm"
                    showLabel={false}
                    variant="icon"
                    className="px-2 py-1"
                  />
                </TooltipWrapper>
              </div>
              
              {/* Spacer to push content to the right */}
              <div className="flex-1" />
              
              {/* Title and navigation - aligned right */}
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* Navigation arrows */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => navigateToRoute('prev')}
                    disabled={getNavigableRoutes().length <= 1}
                    className="p-1 text-gray-400 hover:text-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    title="Previous Route"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigateToRoute('next')}
                    disabled={getNavigableRoutes().length <= 1}
                    className="p-1 text-gray-400 hover:text-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    title="Next Route"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Title - can expand based on content */}
                <div className="text-[10px] md:text-xs font-medium text-white">
                  {selectedRouteId === 'master-route' ? 'Complete Route' : currentRoute?.name}
                </div>
              </div>
              
              {/* Minimize button - separated and always on the far right */}
              {isMobile && (
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 text-gray-400 hover:text-gray-300 transition-colors flex-shrink-0 ml-2"
                  title={isMinimized ? "Expand" : "Minimize"}
                >
                  {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              )}
            </div>
            
            {/* Second row on mobile / inline on desktop: stats */}
            <div className={`${isMobile ? 'hidden' : 'flex items-center space-x-2'}`}>
              <div className="text-gray-500 text-xs"> | </div>
              <div className="flex items-center space-x-3 text-[10px] text-gray-300">
                <span className="flex items-center space-x-1">
                  <ArrowRight className="w-3 h-3" />
                  <strong>{Math.round(currentRoute?.stats.distance)} km</strong>
                </span>
                <span className="flex items-center space-x-1">
                  <ArrowUpRight className="w-3 h-3" />
                  <strong>{currentRoute?.elevation.up.toLocaleString()}m</strong>
                </span>
                <span className="flex items-center space-x-1">
                  <ArrowDownRight className="w-3 h-3" />
                  <strong>{currentRoute?.elevation.down.toLocaleString()}m</strong>
                </span>
                <span className="flex items-center space-x-1">
                  <RectangleEllipsis className="w-3 h-3" />
                  <strong>
                    {currentRoute?.stats.surfaceBreakdown ? 
                      `${Math.round(currentRoute.stats.surfaceBreakdown.unpaved.percentage)}%` :
                      `${Math.round(currentRoute?.stats.unpaved || 0)}%`
                    }
                  </strong>
                </span>
              </div>
              
              {/* Minimize button for desktop - after stats */}
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 text-gray-400 hover:text-gray-300 transition-colors ml-auto"
                title={isMinimized ? "Expand" : "Minimize"}
              >
                {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Stats row for mobile - aligned right */}
            {isMobile && (
              <div className="flex items-center justify-end space-x-3 text-[10px] text-gray-300 w-full pr-1">
                <span className="flex items-center space-x-1">
                  <ArrowRight className="w-3 h-3" />
                  <strong>{Math.round(currentRoute?.stats.distance)} km</strong>
                </span>
                <span className="flex items-center space-x-1">
                  <ArrowUpRight className="w-3 h-3" />
                  <strong>{currentRoute?.elevation.up.toLocaleString()}m</strong>
                </span>
                <span className="flex items-center space-x-1">
                  <ArrowDownRight className="w-3 h-3" />
                  <strong>{currentRoute?.elevation.down.toLocaleString()}m</strong>
                </span>
                <span className="flex items-center space-x-1">
                  <RectangleEllipsis className="w-3 h-3" />
                  <strong>
                    {currentRoute?.stats.surfaceBreakdown ? 
                      `${Math.round(currentRoute.stats.surfaceBreakdown.unpaved.percentage)}%` :
                      `${Math.round(currentRoute?.stats.unpaved || 0)}%`
                    }
                  </strong>
                </span>
              </div>
            )}
          </div>
          
          {!isMinimized && (
            <div className="flex-1 overflow-hidden">
              {activeTab === 'elevation' ? (
              currentRoute ? (
                <ElevationProfile 
                  route={currentRoute}
                  onClose={() => setShowElevation(false)}
                  pois={pois}
                  isMasterRoute={selectedRouteId === 'master-route'}
                  segmentRoutes={routes}
                  onHoverDistanceChange={onHoverDistanceChange}
                  onClimbHover={onClimbHover}
                  className="h-full"
                  scaleFactor={scaleFactor}
                  flybyProgress={flybyProgress}
                />
              ) : (
                <div className="text-center text-gray-400 py-8">
                  <p className="text-lg">No Route Selected</p>
                  <p className="text-sm mt-2">Select a route to view its elevation profile</p>
                </div>
              )
            ) : activeTab === 'segment' ? (
              <RouteOverviewTab
                routeInfo={routeInfo}
                selectedRouteId={selectedRouteId}
                getCurrentRoute={getCurrentRoute}
                pois={pois}
                handlePOISelect={handlePOISelect}
                userPOIs={userPOIs}
                showUserPOIs={showUserPOIs}
                onUserPOIClick={onUserPOIClick}
                showResolved={showResolved}
                onToggleResolved={onToggleResolved}
              />
            ) : activeTab === 'discussion' ? (
              <DiscussionTab
                routeId={routeId || ''}
                routes={routes}
                selectedRouteId={selectedRouteId}
                getCurrentRoute={getCurrentRoute}
                userPOIs={userPOIs}
                showUserPOIs={showUserPOIs}
                onUserPOIClick={onUserPOIClick}
                showResolved={showResolved}
                onToggleResolved={onToggleResolved}
              />
            ) : null}
            </div>
          )}
        </div>
    </div>
    
    {/* GPX Download Modal - Outside the elevation profile container */}
    <GPXDownloadModal
      isOpen={showGPXModal}
      onClose={() => setShowGPXModal(false)}
      onConfirm={() => {
        if (currentRoute) {
          downloadGPX(currentRoute)
        }
      }}
      route={currentRoute}
      routeType={selectedRouteId === 'master-route' ? 'full' : 'segment'}
      parentRouteName={selectedRouteId !== 'master-route' && routes.length > 1 ? routeInfo.name : undefined}
    />
    </>
  )
}

interface RouteOverviewTabProps {
  routeInfo: { 
    description: string
    routes?: Array<{ id: string; description?: string }> // Add segment descriptions
  }
  selectedRouteId: string
  getCurrentRoute: () => RouteData | null
  pois: POI[]
  handlePOISelect: (poi: POI) => void
  userPOIs?: any[]
  showUserPOIs?: boolean
  onUserPOIClick?: (poi: any) => void
  showResolved?: boolean
  onToggleResolved?: () => void
}

function RouteOverviewTab({
  routeInfo,
  selectedRouteId,
  getCurrentRoute,
  pois,
  handlePOISelect,
  userPOIs = [],
  showUserPOIs = false,
  onUserPOIClick,
  showResolved = false,
  onToggleResolved
}: RouteOverviewTabProps) {
  // Get the current route to check for segment-specific description
  const currentRoute = getCurrentRoute()
  const isSegment = selectedRouteId !== 'master-route' && currentRoute
  
  // First try to get description from routeInfo.routes (for embed), then from currentRoute, then default
  let displayDescription = routeInfo.description
  if (isSegment) {
    // Check if we have segment descriptions in routeInfo.routes
    const segmentInfo = routeInfo.routes?.find(r => r.id === selectedRouteId)
    if (segmentInfo?.description) {
      displayDescription = segmentInfo.description
    } else if (currentRoute.description) {
      displayDescription = currentRoute.description
    }
  }
  const [expandedSection, setExpandedSection] = useState<'overview' | 'pois' | null>('overview') // Default to overview open
  const [isTabletOrSmaller, setIsTabletOrSmaller] = useState(false)

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsTabletOrSmaller(window.innerWidth < 1024) // lg breakpoint - tablets in portrait and phones
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  // Desktop layout (70/30 split)
  if (!isTabletOrSmaller) {
    return (
      <div className="p-3 h-full">
        <div className="flex gap-6">
          {/* Left side - Route Description (70%) */}
          <div className="flex-[7] flex flex-col">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Route Overview
            </label>
            
            <div 
              className="w-full p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg text-gray-100 overflow-y-auto"
              style={{ 
                height: '400px' // Increased by 100px total
              }}
            >
              <div 
                className="prose prose-invert prose-sm max-w-none [&_a]:text-cyan-500 [&_a:hover]:text-cyan-400"
                dangerouslySetInnerHTML={{ 
                  __html: displayDescription || '<p class="text-gray-400">No description available for this route.</p>' 
                }}
              />
            </div>
          </div>

          {/* Right side - POI Cheat Sheet (30%) */}
          <div className="flex-[3]">
            <POIReferencePanelWithReports
              selectedRouteId={selectedRouteId}
              getCurrentRoute={getCurrentRoute}
              pois={pois}
              selectPOI={handlePOISelect}
              height="400px" // Increased by 30px
              userPOIs={userPOIs}
              showUserPOIs={showUserPOIs}
              onUserPOIClick={onUserPOIClick}
              showResolved={showResolved}
              onToggleResolved={onToggleResolved}
            />
          </div>
        </div>
      </div>
    )
  }

  // Tablet/Mobile layout (Accordions)
  return (
    <div className="p-3 h-full overflow-y-auto">
      <div className="space-y-3">
        {/* POI Reference Accordion - First */}
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'pois' ? null : 'pois')}
            className="w-full px-4 py-3 bg-white/10 hover:bg-white/15 transition-colors flex items-center justify-between backdrop-blur-md"
          >
            <span className="text-sm font-medium text-gray-100">Points of Interest</span>
            {expandedSection === 'pois' ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'pois' && (
            <div className="p-4 bg-transparent">
              <POIReferencePanelWithReports
                selectedRouteId={selectedRouteId}
                getCurrentRoute={getCurrentRoute}
                pois={pois}
                selectPOI={handlePOISelect}
                height="auto"
                userPOIs={userPOIs}
                showUserPOIs={showUserPOIs}
                onUserPOIClick={onUserPOIClick}
                showResolved={showResolved}
                onToggleResolved={onToggleResolved}
              />
            </div>
          )}
        </div>

        {/* Route Overview Accordion - Second */}
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'overview' ? null : 'overview')}
            className="w-full px-4 py-3 bg-white/10 hover:bg-white/15 transition-colors flex items-center justify-between backdrop-blur-md"
          >
            <span className="text-sm font-medium text-gray-100">Route Overview</span>
            {expandedSection === 'overview' ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
          
          {expandedSection === 'overview' && (
            <div className="p-4 bg-transparent">
              <div 
                className="prose prose-invert prose-sm max-w-none [&_a]:text-cyan-500 [&_a:hover]:text-cyan-400"
                dangerouslySetInnerHTML={{ 
                  __html: displayDescription || '<p class="text-gray-400">No description available for this route.</p>' 
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}